import {
  CaseDocument,
  CaseDocumentStatus,
  isEndpointLockError,
  useLazyGetCaseDocumentsQuery,
  useLazyGetCaseProvidersQuery,
  useLazyGetCaseStatusQuery,
  useLazyGetDocumentGetDownloadQuery,
  useLazyPostDocumentDeleteQuery,
  useLazyPostDocumentProviderAddNewQuery,
  useLazyPostDocumentProviderAddQuery,
} from '@/api';
import { useAzureFileUpload } from '@repo/file-uploader';
import {
  selectCaseDocumentsList,
  selectCaseDocumentsPagination,
  selectCaseDocumentsRowCount,
  selectCaseIdData,
  selectCaseProvidersList,
  selectCaseStatus,
  selectUploadingDocumentIds,
  setCaseDocumentsPagination,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  DownloadIcon,
  DataTableProgressBarRow,
  PdfIcon,
  StartGdprCheckButton,
  UserAvatar,
  IndeterminateCheckbox,
  useGlobalToast,
} from '@repo/atomic-components';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import type { ToastMessage } from 'primereact/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccessControlAction, AppRoutes, CASE_DOCUMENT_DELETE_CONFIRM } from '@/constants';
import { useAccessControl, useConfirmDocumentDelete, useStartGdprCheck } from '@/hooks';
import {
  convertLodashConstantToString,
  openNewTab,
  CaseDocumentGdprStatus,
  isDocumentDeleteBlockedByCaseStatus,
} from '@/utils';
import { useFletchersBroadcast } from '@/broadcast/hooks';
import { BROADCAST_GDPR_CHECK_STARTED_FROM_CASE } from '@/broadcast/constants';
import { DeleteDocumentButton } from '@/ui/components/DeleteDocumentButton';
import { DocumentProviderCell } from '@/ui/components/DocumentProviderCell';
import { DocumentStatusCell } from '@/ui/components/DocumentStatusCell';
import { TableIconButton } from '@/ui/components/TableIconButton';

const CASE_DOCUMENTS_POLLING_DELAY = 30 * 1000; // 30 seconds

export interface UploadDocumentsTableRow extends CaseDocument {
  progressBars?: DataTableProgressBarRow[];
}

export const useUploadDocumentsTable = () => {
  const dispatch = useAppDispatch();

  const [rowSelection, setRowSelection] = useState({});

  const caseId = useAppSelector(selectCaseIdData);
  const uploadingDocumentIds = useAppSelector(selectUploadingDocumentIds);
  const caseProvidersList = useAppSelector(selectCaseProvidersList);

  const { uploadList, downloadFile } = useAzureFileUpload();

  const caseDocumentsList = useAppSelector(selectCaseDocumentsList);
  const caseDocumentsRowCount = useAppSelector(selectCaseDocumentsRowCount);
  const caseDocumentsPagination = useAppSelector(selectCaseDocumentsPagination);

  const caseStatus = useAppSelector(selectCaseStatus);

  const isUploadDeleteRawMedicalRecords = useAccessControl(AccessControlAction.UPLOAD_DELETE_RAW_MEDICAL_RECORDS);
  const isExportRawMedicalRecords = useAccessControl(AccessControlAction.EXPORT_RAW_MEDICAL_RECORDS);

  const isDeleteBlocked = isDocumentDeleteBlockedByCaseStatus(caseStatus?.status);

  const [triggerGetCaseDocuments, { isFetching: getCaseDocumentsFetching, currentData: caseDocumentsCurrentData }] =
    useLazyGetCaseDocumentsQuery();
  const [triggerGetDocumentGetDownload] = useLazyGetDocumentGetDownloadQuery();
  const [triggerPostDocumentDelete] = useLazyPostDocumentDeleteQuery();
  const [triggerPostDocumentProviderAdd] = useLazyPostDocumentProviderAddQuery();
  const [triggerPostDocumentProviderAddNew] = useLazyPostDocumentProviderAddNewQuery();

  const [triggerGetCaseProviders] = useLazyGetCaseProvidersQuery();
  const [triggerGetCaseStatus] = useLazyGetCaseStatusQuery();

  const globalToastRef = useGlobalToast();
  const confirmDocumentDelete = useConfirmDocumentDelete();
  const { startGdprCheck, startingGdprCheckDocumentIds } = useStartGdprCheck();
  const { sendMessageWithoutResponse } = useFletchersBroadcast();

  // Provider-selector popup state — anchored to the row's provider
  // cell on click. Replaces the legacy multi-step modal dialog so
  // this table matches the MedicalRecordsPage popup pattern.
  const [providerSelectorVisible, setProviderSelectorVisible] = useState(false);
  const [providerSelectorPosition, setProviderSelectorPosition] = useState({ top: 0, left: 0 });
  // Rows whose providers will be updated by the next popup selection.
  // Captured at click time so multi-row selection survives the popup
  // open → select → resolve handshake.
  const [providerSelectorPendingRows, setProviderSelectorPendingRows] = useState<string[]>([]);

  const [data, setData] = useState<UploadDocumentsTableRow[]>([]);

  useEffect(() => {
    triggerGetCaseProviders({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCaseDocumentsRequest = useCallback(() => {
    if (caseId) {
      return triggerGetCaseDocuments(
        {
          caseId: caseId,
          ...caseDocumentsPagination,
        },
        false,
      );
    }
  }, [caseDocumentsPagination, caseId, triggerGetCaseDocuments]);

  useEffect(() => {
    getCaseDocumentsRequest();
  }, [getCaseDocumentsRequest]);

  const isAiProcessingComplete =
    caseDocumentsList.length > 0 &&
    caseDocumentsList.every((caseDocument) => caseDocument.status === CaseDocumentStatus.AI_PROCESSING_COMPLETE);

  // Poll so document statuses changed elsewhere (e.g. a GDPR check started or completed) show up.
  // Statuses don't change once AI processing is complete, so polling stops there.
  useEffect(() => {
    if (isAiProcessingComplete) return;
    const intervalId = setInterval(getCaseDocumentsRequest, CASE_DOCUMENTS_POLLING_DELAY);
    return () => clearInterval(intervalId);
  }, [getCaseDocumentsRequest, isAiProcessingComplete]);

  useEffect(() => {
    const caseDocumentsListWithProgressBars = caseDocumentsList.map((caseDocument) => {
      const isUploading = uploadingDocumentIds.includes(caseDocument.documentId);

      if (isUploading) {
        const uploadInfo = uploadList.find((azureFileUpload) => azureFileUpload.id === caseDocument.documentId);
        if (uploadInfo) {
          return {
            ...caseDocument,
            progressBars: [{ id: uploadInfo.id, value: uploadInfo.progress }],
          };
        }
        return caseDocument;
      }
      return caseDocument;
    });

    setData(caseDocumentsListWithProgressBars);
  }, [caseDocumentsList, uploadingDocumentIds, uploadList]);

  const handleDownloadDocumentClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const downloadDocumentId = event.currentTarget.dataset.downloadDocumentId;
      const downloadDocumentFileName = event.currentTarget.dataset.downloadDocumentFilename;

      if (downloadDocumentId) {
        triggerGetDocumentGetDownload({ documentId: downloadDocumentId })
          .unwrap()
          .then((data) => {
            if (downloadDocumentFileName && data && data.downloadPath && data.downloadToken) {
              downloadFile(downloadDocumentFileName, data.downloadPath, data.downloadToken);
            }
          });
      }
    },
    [downloadFile, triggerGetDocumentGetDownload],
  );

  const handleDeleteDocumentClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      const deleteDocumentId = event.currentTarget.dataset.deleteDocumentId;
      if (!deleteDocumentId) return;
      const confirmed = await confirmDocumentDelete({
        dialogId: CASE_DOCUMENT_DELETE_CONFIRM,
        documentId: deleteDocumentId,
      });
      if (!confirmed) return;

      try {
        await triggerPostDocumentDelete({ documentId: deleteDocumentId }).unwrap();
      } catch (error) {
        console.error('Failed to delete document:', error);
        // The lock's own toast has already explained this one
        if (isEndpointLockError(error)) return;
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Delete failed',
          detail: 'Failed to delete the document. Please try again.',
          closable: true,
          life: 5000,
        });
      }
    },
    [confirmDocumentDelete, triggerPostDocumentDelete, globalToastRef],
  );

  const handleGdprCheckClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      const documentId = event.currentTarget.dataset.documentId;
      const checkStarted = event.currentTarget.dataset.checkStarted === 'true';
      if (!documentId) return;

      const fetchLatestDocument = async () => {
        const caseDocuments = await getCaseDocumentsRequest()?.unwrap();
        return caseDocuments?.documents.find((document) => document.documentId === documentId);
      };

      if (await startGdprCheck(documentId, checkStarted, fetchLatestDocument)) {
        sendMessageWithoutResponse(BROADCAST_GDPR_CHECK_STARTED_FROM_CASE, {});
        openNewTab(`/${AppRoutes.GdprCheck}/${documentId}?from=case`);
      }
    },
    [startGdprCheck, getCaseDocumentsRequest, sendMessageWithoutResponse],
  );

  // Anchored popup positioning logic ported from
  // MedicalRecordsPage's useMedicalRecordsListTable: flip above if
  // there isn't room below, clamp horizontally inside the viewport.
  const handleSelectProviderClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    let selectedRows = Object.keys(table.getState().rowSelection);
    if (!selectedRows.includes(rowId)) {
      selectedRows = [...selectedRows, rowId];
    }
    setProviderSelectorPendingRows(selectedRows);

    const rect = event.currentTarget.getBoundingClientRect();
    const popoverWidth = 480; // matches ProviderSelectorPopup minWidth
    const popoverHeight = 400;
    const pagePadding = 24;
    let left = rect.left;
    let top = rect.bottom + window.scrollY + 4;

    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < popoverHeight && rect.top > spaceBelow) {
      top = rect.top + window.scrollY - popoverHeight - 4;
    }

    if (rect.left + popoverWidth + pagePadding > window.innerWidth) {
      left = window.innerWidth - popoverWidth - pagePadding;
    }

    setProviderSelectorPosition({ top, left });
    setProviderSelectorVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Popup-resolved provider selection. Two flows: pick an existing
  // provider (selectedProviderId set) → POST .../add; create a new
  // one (selectedProviderName + selectedProviderType set) → POST
  // .../addNew, then refetch.
  const handleProviderSelect = useCallback(
    (selectedProviderId: string, selectedProviderName: string, selectedProviderType: string) => {
      const pendingRows = providerSelectorPendingRows;
      if (!pendingRows.length) return;
      if (!selectedProviderId && !(selectedProviderName && selectedProviderType)) return;

      // Per-row requests can resolve in any order, so the refetch must
      // wait on Promise.all rather than the array-last row's `.then` —
      // otherwise the table reloads while earlier rows are still in
      // flight and renders stale provider data.
      const refetchAll = () => {
        getCaseDocumentsRequest();
        triggerGetCaseProviders({});
        if (caseId) {
          triggerGetCaseStatus({ caseId });
        }
      };

      const updatingProvidersToast: ToastMessage = {
        severity: 'info',
        summary: 'Updating providers',
        detail: 'Please wait while providers are being assigned.',
        life: 10000,
      };
      globalToastRef.current?.show(updatingProvidersToast);

      if (selectedProviderId) {
        Promise.all(
          pendingRows.map((selectedRowId) => {
            const selectedRowData = table.getRow(selectedRowId).original;
            return triggerPostDocumentProviderAdd({
              documentId: selectedRowData.documentId,
              providerId: selectedProviderId,
            }).unwrap();
          }),
        )
          .then(() => {
            refetchAll();
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'success',
              summary: 'Providers updated',
              life: 5000,
            });
          })
          .catch((error) => {
            console.error('Provider assignment failed:', error);
            // Clearing would also take down the lock's own toast, which has already explained this one
            if (isEndpointLockError(error)) {
              globalToastRef.current?.remove(updatingProvidersToast);
              return;
            }
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Provider update failed',
              detail: 'Please try again.',
              closable: true,
              life: 5000,
            });
          });
        table.resetRowSelection();
        return;
      }

      if (selectedProviderName && selectedProviderType) {
        // For "create new", the first call creates the provider and
        // returns its new id; the remaining calls reuse that id via
        // the existing-provider endpoint.
        const [firstRowId, ...remainingRowIds] = pendingRows;
        const firstRowData = table.getRow(firstRowId).original;
        let isProviderCreated = false;
        triggerPostDocumentProviderAddNew({
          documentId: firstRowData.documentId,
          providerName: selectedProviderName,
          providerType: selectedProviderType,
          invalidateData: !remainingRowIds.length,
        })
          .unwrap()
          .then(({ newProviderId }) => {
            isProviderCreated = true;
            if (!remainingRowIds.length) {
              refetchAll();
              globalToastRef.current?.clear();
              globalToastRef.current?.show({
                severity: 'success',
                summary: 'Providers updated',
                life: 5000,
              });
              return;
            }
            return Promise.all(
              remainingRowIds.map((selectedRowId) => {
                const selectedRowData = table.getRow(selectedRowId).original;
                return triggerPostDocumentProviderAdd({
                  documentId: selectedRowData.documentId,
                  providerId: newProviderId,
                }).unwrap();
              }),
            ).then(() => {
              refetchAll();
              globalToastRef.current?.clear();
              globalToastRef.current?.show({
                severity: 'success',
                summary: 'Providers updated',
                life: 5000,
              });
            });
          })
          .catch((error) => {
            console.error('Provider assignment failed:', error);
            // The first row already has the new provider even though the rest failed, so show it
            if (isProviderCreated) refetchAll();
            if (isEndpointLockError(error)) {
              globalToastRef.current?.remove(updatingProvidersToast);
              return;
            }
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Provider update failed',
              detail: 'Please try again.',
              closable: true,
              life: 5000,
            });
          });
        table.resetRowSelection();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      providerSelectorPendingRows,
      caseId,
      getCaseDocumentsRequest,
      triggerGetCaseProviders,
      triggerGetCaseStatus,
      triggerPostDocumentProviderAdd,
      triggerPostDocumentProviderAddNew,
      globalToastRef,
    ],
  );

  const columns = useMemo<ColumnDef<UploadDocumentsTableRow, unknown>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        accessorKey: 'documentName',
        header: 'Document',
        cell: (info) => {
          const fileName = info.getValue() as string;
          const row = info.row.original;
          const fileSize = row.sizeMb ? parseFloat(String(row.sizeMb)).toFixed(2) : null;
          const pageCount = row.pageCount;

          if (!fileName) return '';

          const displayName = fileName.length > 24 ? `${fileName.slice(0, 24)}...` : fileName;

          return (
            <div className="flex items-center gap-2">
              <PdfIcon className="h-4 w-4 shrink-0" />
              <div className="flex flex-col">
                <span
                  className="block overflow-hidden font-semibold overflow-ellipsis whitespace-nowrap"
                  title={fileName}
                >
                  {displayName}
                </span>
                <div className="flex items-center gap-2">
                  {pageCount !== null && pageCount !== undefined && pageCount > 0 && (
                    <span className="text-fletchers-gray-250 text-[12px]/[16px]">
                      {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                    </span>
                  )}
                  {fileSize && <span className="text-fletchers-gray-250 text-[12px]/[16px]">{fileSize} MB</span>}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'uploadedBy',
        header: 'Uploaded by',
        cell: (info) => {
          const row = info.row.original;
          const userName = row.uploadedBy?.userName || '';
          if (!userName) return '';
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <div className="flex flex-col">
                <span
                  className="block overflow-hidden overflow-ellipsis whitespace-nowrap"
                  title={userName}
                  style={{
                    maxWidth: row.uploadedAt
                      ? `${format(parseISO(row.uploadedAt), 'HH:mm d MMM y').length * 7}px`
                      : '300px',
                  }}
                >
                  {userName}
                </span>
                <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">
                  {row.uploadedAt ? format(parseISO(row.uploadedAt), 'HH:mm d MMM y') : ''}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'providerDataName',
        header: 'Provider',
        cell: (info) => {
          const rowId = info.row.id;
          const row = info.row.original;
          return (
            <DocumentProviderCell
              rowId={rowId}
              providerName={row.providerData?.providerName ?? ''}
              providerType={row.providerData?.providerType ?? ''}
              onSelectProviderClick={handleSelectProviderClick}
            />
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const stateValue = info.getValue() as string;
          if (!stateValue) return '';
          // The case-documents endpoint returns the SCREAMING_SNAKE_CASE
          // enum (e.g. "UPLOAD_FAILED") whereas the medical-records
          // endpoint returns the user-facing form ("Upload failed").
          // Normalise so DocumentStatusCell's matchers compare against
          // the same shape both tables use.
          const humanReadable = convertLodashConstantToString(stateValue);
          return (
            <DocumentStatusCell
              status={humanReadable}
              failureReason={info.row.original.documentProcessingFailureReason}
            />
          );
        },
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: (info) => {
          const rowValue = info.row.original;
          return (
            <div className="flex flex-nowrap items-center justify-end gap-2">
              {!rowValue.checkCompleted &&
                (rowValue.status === CaseDocumentGdprStatus.READY_FOR_GDPR_CHECK ||
                  rowValue.status === CaseDocumentGdprStatus.GDPR_CHECK_IN_PROGRESS) && (
                  <StartGdprCheckButton
                    documentId={rowValue.documentId}
                    providerId={rowValue.providerData ? rowValue.providerData.providerId : null}
                    checkStarted={!!rowValue.checkStarted}
                    disabled={startingGdprCheckDocumentIds.includes(rowValue.documentId)}
                    onClick={handleGdprCheckClick}
                  />
                )}
              {rowValue.canDownload && isExportRawMedicalRecords && (
                <TableIconButton
                  title="Download document"
                  data-download-document-id={rowValue.documentId}
                  data-download-document-filename={rowValue.documentName}
                  onClick={handleDownloadDocumentClick}
                >
                  <DownloadIcon className="text-fletchers-black-900" />
                </TableIconButton>
              )}
              {isUploadDeleteRawMedicalRecords && (
                <DeleteDocumentButton
                  title="Delete document"
                  isBlocked={isDeleteBlocked}
                  data-delete-document-id={rowValue.documentId}
                  onClick={handleDeleteDocumentClick}
                />
              )}
            </div>
          );
        },
      },
    ],
    [
      handleDeleteDocumentClick,
      handleDownloadDocumentClick,
      handleGdprCheckClick,
      handleSelectProviderClick,
      isDeleteBlocked,
      isExportRawMedicalRecords,
      isUploadDeleteRawMedicalRecords,
      startingGdprCheckDocumentIds,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination: caseDocumentsPagination,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    rowCount: caseDocumentsRowCount,
    onPaginationChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newPaginationState = updaterOrValue(caseDocumentsPagination);
        dispatch(setCaseDocumentsPagination(newPaginationState));
      } else {
        dispatch(setCaseDocumentsPagination(updaterOrValue));
      }
    },
    manualPagination: true,
  });

  return {
    table,
    // Only while the current page has nothing loaded yet, so background refreshes (polling,
    // GDPR status checks) don't show the spinner over rows that are still usable.
    isTableDataLoading: getCaseDocumentsFetching && !caseDocumentsCurrentData,
    providerSelectorVisible,
    setProviderSelectorVisible,
    providerSelectorPosition,
    caseProvidersList,
    handleProviderSelect,
  };
};
